import React, { useCallback, useEffect, useRef, useState } from 'react';
import target300A from '../images/G300A10.svg';
import target300B from '../images/G300B4.svg';
import target50KK from '../images/G50KK.svg';
import { FinalCompetitionPhase, LaneData, TargetType } from '../models/Models';
import { ReactSVG } from 'react-svg';
import './FinalCompetition.css';

// ── ReactSVG wrapper style — module-level constant so the object reference is always
// the same. ReactSVG uses shallowDiffers() on ALL props in componentDidUpdate; if any
// prop reference changes it re-injects the SVG. A new style literal on every render
// would cause infinite re-injection → setState loop → site hang.
const REACT_SVG_WRAPPER_STYLE: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
};

// ── Target-specific zoom configuration (module-level: created once, not per render) ──
//
// Shot coordinates (XPosition/YPosition) are always in integer mm, as produced by all
// connectors (TG3000: raw_hex/10 → mm; Styx: metres * 1000 → mm).
//
// Both SVGs use a shared coordinate system (1 unit = 1 mm) and group transform scale(1,-1).
// G50KK uses viewBox="-500 -500 1000 1000" (symmetric ±500).
// G300A10 uses viewBox="-500 -500 1020 1020" (extends to +520 on positive axes).
// The zoom formula uses 500 as the conservative limit (the negative-side extent of both),
// ensuring shots stay within the viewBox on all sides for any zoom level.
//
// G300A10 / G300B4 ring boundaries (mm):
//   10=50, 9=100, 8=150, 7=200, 6=250, 5=300, 4=350, 3=400, 2=450, 1=500
//
// G50KK ring boundaries (mm, SVG redesigned from 0.1 mm → 1 mm coordinate system):
//   10=5.2, 9=13.2, 8=21.2, 7=29.2, 6=37.2, 5=45.2, 4=53.2,
//   aiming_mark=56.2, 3=61.2, 2=69.4, 1/outer=77.2

interface ZoomLevel {
    /** Apply this level when maxDist (mm) is at most this value */
    maxDistMm: number;
    /** Scale factor applied to the SVG group; replaces the group's built-in transform */
    scale: number;
}

// 3 levels: Rings 10–8 | Rings 7–5 | Rings 4–1
//
// Threshold formula: (maxDistMm + shotRadius) * scale ≤ 500
// ensures both the shot centre AND its visible edge remain within the ±500 viewBox.
//
//   G300A10 (radius=15): L1: (150+15)*3=495 ✓  L2: (235+15)*2=500 ✓ exactly
//   G50KK   (radius=3):  L1: (17+3)*24=480 ✓   L2: (42+3)*11=495 ✓
const ZOOM_LEVELS_300M: readonly ZoomLevel[] = [
    { maxDistMm: 150, scale: 3 },        // 8-ring=150mm → (150+15)*3=495 ≤ ±500 viewBox
    { maxDistMm: 235, scale: 2 },        // ~6-ring=235mm → (235+15)*2=500 ≤ ±500 viewBox
    { maxDistMm: Infinity, scale: 1 },   // Full target visible (rings 4–1)
];
const ZOOM_LEVELS_KK50M: readonly ZoomLevel[] = [
    { maxDistMm: 17,  scale: 24 },       // 8-ring≈21mm → (17+3)*24=480 ≤ ±500 viewBox
    { maxDistMm: 42,  scale: 11 },       // 5-ring≈45mm → (42+3)*11=495 ≤ ±500 viewBox
    { maxDistMm: Infinity, scale: 6 },   // outer=77.2mm → 77.2*6=463 ≤ 500 (full target visible, no clipping)
];

// Shot circle radius in mm (same coordinate space as ring radii after G50KK redesign).
// G300A10/G300B4: 15mm is large enough to be visible at zoom=1–3 without obscuring rings.
// G50KK: 3mm ≈ .22LR bullet radius (5.6mm dia / 2 ≈ 2.8mm) — visible at zoom 7–24,
//         small enough not to overwhelm the compact 50m ring structure.
const SHOT_RADIUS_300M = 15;
const SHOT_RADIUS_KK50M = 3;

interface FinalCompetitionTargetProps {
    laneData: LaneData | undefined | null;
    numberOfCompetitionShots: number;
    displayTargetType: TargetType | undefined;
    /** Current phase of the final — used to dim empty tiles during competition/elimination. */
    currentFinalPhase: FinalCompetitionPhase | undefined;
    /** Dense rank computed by parent FinalCompetitionView (1-based, ties allowed). */
    rank: number;
}

/**
 * Single target panel for the ISSF Elimination Final screen.
 *
 * Layout (flex column):
 *   - SVG target image (flex: 1 — fills all remaining space, scales to any resolution)
 *   - Header row: [lane# + name] [last shot value in red, 1.2×] [rank in red, xxx-large] [total]
 *   - Competition shot grid: 5 cols × ceil(N/5) rows (hidden during elimination phase)
 *   - Elimination row: 1 row × 2 cells (shown only during elimination phase)
 */
const FinalCompetitionTarget: React.FC<FinalCompetitionTargetProps> = ({
    laneData,
    numberOfCompetitionShots,
    displayTargetType,
    currentFinalPhase,
    rank,
}) => {
    const shots = laneData?.shots ?? [];

    // Phase detection
    const competitionShots = shots.filter(s => (s.shotNr ?? 0) <= numberOfCompetitionShots);
    const eliminationShots = shots.filter(s => (s.shotNr ?? 0) > numberOfCompetitionShots);
    const isElimination = eliminationShots.length > 0;
    const lastShot = shots.length > 0 ? shots[shots.length - 1] : null;

    // Round-aware current pair for elimination shots.
    // Each elimination round = exactly 2 shots. Instead of naively slicing the last 2 shots
    // (which would span two rounds when a new round starts), we calculate the start index of
    // the current round: floor((length - 1) / 2) * 2.
    //   1 shot  → start=0 → [shot0]          (round 1, shot 1 in progress)
    //   2 shots → start=0 → [shot0, shot1]   (round 1 complete)
    //   3 shots → start=2 → [shot2]          (round 2, shot 1 in progress)
    //   4 shots → start=2 → [shot2, shot3]   (round 2 complete)
    const currentElimPairStart = eliminationShots.length > 0
        ? Math.floor((eliminationShots.length - 1) / 2) * 2
        : 0;
    const currentElimPairShots = eliminationShots.slice(currentElimPairStart, currentElimPairStart + 2);

    // SVG source selection: B4 = TargetType.Value3 (3), KK50m = TargetType.Value4 (4), everything else = A10
    const targetSvg =
        displayTargetType === TargetType.Value3 ? target300B :
        displayTargetType === TargetType.Value4 ? target50KK :
        target300A;
    const svgGroupId =
        displayTargetType === TargetType.Value3 ? 'G300B4' :
        displayTargetType === TargetType.Value4 ? 'G50KK' :
        'G300A10';

    // Shots to display on SVG:
    // - Elimination phase: current round's pair only (not a rolling last-2 window, see above)
    // - Competition phase: only shots in the current group of 5 (cleared when a new group starts).
    //   e.g. shots 1-5 form group 1; when shot 6 arrives the target is cleared and only shot 6 is shown.
    //   Formula: groupStart = floor((latestShotNr - 1) / 5) * 5 + 1
    //     shot 1-5  → groupStart=1  → shows shotNr 1-5
    //     shot 6    → groupStart=6  → clears target, shows shotNr 6 only
    //     shot 6-10 → groupStart=6  → shows shotNr 6-10
    const latestCompShotNr = competitionShots.length > 0
        ? (competitionShots[competitionShots.length - 1]?.shotNr ?? 0)
        : 0;
    const currentGroupStart = latestCompShotNr > 0
        ? Math.floor((latestCompShotNr - 1) / 5) * 5 + 1
        : 1;
    const currentGroupEnd = currentGroupStart + 4;
    const svgShots = isElimination
        ? currentElimPairShots
        : competitionShots.filter(s => {
            const nr = s.shotNr ?? 0;
            return nr >= currentGroupStart && nr <= currentGroupEnd;
        });

    // Competition grid dimensions
    const gridRows = Math.ceil(numberOfCompetitionShots / 5);

    // Tenth-scoring detection: if any shot has a non-integer value the discipline uses tenths.
    // When true, ALL numeric values (shots + total) are formatted with exactly 1 decimal place
    // so that integer results like 8 are shown as "8.0" for consistency.
    const isTenthScoring = shots.some(s => s.value != null && !Number.isInteger(s.value));
    const formatValue = (v: number | null | undefined): string => {
        if (v == null) return '\u00A0';
        return isTenthScoring ? v.toFixed(1) : String(v);
    };
    const formatTotal = (v: number | null | undefined): string => {
        if (v == null) return '';
        return isTenthScoring ? v.toFixed(1) : (Number.isInteger(v) ? String(v) : v.toFixed(1));
    };

    // ── Select zoom config and shot radius for the active target type ────────
    const isKK50m = displayTargetType === TargetType.Value4;
    const zoomLevels = isKK50m ? ZOOM_LEVELS_KK50M : ZOOM_LEVELS_300M;
    const shotCircleRadius = isKK50m ? SHOT_RADIUS_KK50M : SHOT_RADIUS_300M;

    // ── zoomScale computed in render so it is available in both beforeInjection
    // (for correct initial state on mount/remount) and useEffect (for updates).
    // This replaces the per-shot zoom computation that was previously inside beforeInjection.
    const maxDist = svgShots.length > 0
        ? Math.max(...svgShots.map(s =>
            Math.sqrt(Math.pow(s.xPosition ?? 0, 2) + Math.pow(s.yPosition ?? 0, 2))
          ))
        : 0;
    const zoomScale = svgShots.length > 0
        ? (zoomLevels.find(z => maxDist <= z.maxDistMm) ?? zoomLevels[zoomLevels.length - 1]).scale
        : (isKK50m ? ZOOM_LEVELS_KK50M[ZOOM_LEVELS_KK50M.length - 1].scale : 1);

    // ── Stable key: only remounts when lane is reassigned or target type changes.
    // Previously changed on every shot (causing full SVG unmount → visible flicker).
    // Shot circles are now managed via useEffect DOM mutations instead.
    const svgKey = `target-${laneData?.laneNumber ?? 0}-${displayTargetType ?? 0}`;

    // ── Mutable ref that always holds the current render values.
    // Lets the stable beforeInjection callback (useCallback, empty deps) read
    // up-to-date values without closing over stale state.
    const renderValuesRef = useRef({ zoomScale, svgGroupId, isKK50m });
    renderValuesRef.current = { zoomScale, svgGroupId, isKK50m };

    // ── Ref to the injected SVG DOM element — updated by beforeInjection.
    const svgRef = useRef<SVGSVGElement | null>(null);

    // ── Counter incremented each time beforeInjection fires.
    // Adding it to useEffect deps causes the effect to re-run after async SVG
    // injection — necessary because ReactSVG loads SVGs asynchronously, so the
    // first useEffect run finds svgRef.current === null and exits early.
    const [svgInjectionCount, setSvgInjectionCount] = useState(0);

    // ── Ref tracking the previous shot list — used to gate the flash animation
    // restart so it only fires when new shots actually arrive, not on every
    // parent re-render that produces a new svgShots array reference.
    const prevShotsRef = useRef<typeof svgShots>([]);

    // ── useEffect: update zoom transform + shot circles directly in the SVG DOM.
    // Runs whenever shots, zoom level, or injection count change.
    // No SVG remount → no flicker.
    useEffect(() => {
        const svg = svgRef.current;
        // Guard 1: ref not yet set (SVG still loading asynchronously)
        // Guard 2: isConnected — svgRef may still point to the OLD detached node
        //   during the window between a svgKey change and the new async injection.
        //   DOM mutations on detached nodes are harmless but represent wasted work.
        if (!svg || !svg.isConnected) return;

        const img = svg.children.namedItem(svgGroupId);
        if (!img) return;

        // Update ring group zoom (mirrors / scales the coordinate system)
        img.setAttribute('transform', `scale(${zoomScale}, -${zoomScale})`);

        // Toggle ring-number text labels (G50KK only; no-op for G300A10/G300B4)
        if (zoomScale > 1) {
            svg.querySelectorAll('text').forEach(el => { (el as SVGElement).style.display = 'none'; });
        } else {
            svg.querySelectorAll('text').forEach(el => { (el as SVGElement).style.display = ''; });
        }

        // KK50m labels group (distinct from ring group)
        if (isKK50m) {
            const labelsGroup = svg.children.namedItem(`${svgGroupId}_labels`);
            labelsGroup?.setAttribute('transform', `scale(${zoomScale}, ${zoomScale})`);
        }

        // Remove previous shot circles (tagged with data-shot to avoid touching SVG rings)
        img.querySelectorAll('circle[data-shot]').forEach(el => el.remove());

        // Append updated shot circles — at most 5 (competition) or 2 (elimination) DOM ops
        svgShots.forEach((shot, idx) => {
            const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            c.setAttribute('data-shot',       String(shot.shotNr ?? idx));
            c.setAttribute('cx',              String(shot.xPosition ?? 0));
            c.setAttribute('cy',              String(shot.yPosition ?? 0));
            c.setAttribute('r',               String(shotCircleRadius));
            c.setAttribute('fill',            idx === svgShots.length - 1 ? 'red' : 'yellow');
            c.setAttribute('shape-rendering', 'geometricPrecision');
            img.appendChild(c);
        });

        // Flash animation on the latest shot circle — only when shots actually changed.
        // svgShots is a new array reference on every render (computed inline), so useEffect
        // runs on every parent re-render. Gating on shot-content equality prevents the
        // animation from being reset by rank/phase/name updates that don't add new shots.
        const prev = prevShotsRef.current;
        const shotsChanged =
            svgShots.length !== prev.length ||
            svgShots.some((s, i) => s.shotNr !== prev[i]?.shotNr);
        prevShotsRef.current = svgShots;

        if (shotsChanged) {
            const lastCircle = img.querySelector('circle[data-shot]:last-child');
            if (lastCircle) {
                // Force animation restart: remove then re-add the class
                lastCircle.removeAttribute('class');
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (lastCircle as SVGElement).getBoundingClientRect(); // trigger reflow
                lastCircle.setAttribute('class', 'fc-shot-latest');
            }
        }
    }, [svgShots, svgGroupId, zoomScale, isKK50m, shotCircleRadius, svgInjectionCount]);

    // ── Stable beforeInjection callback (empty deps — reference never changes).
    // ReactSVG re-injects whenever its props change. A stable reference ensures
    // ReactSVG does NOT re-inject on every shot/zoom update — preventing the
    // setState→re-render→ new function ref→ReactSVG re-injects→setState loop.
    // Current render values are read from renderValuesRef so there is no stale-closure issue.
    const handleBeforeInjection = useCallback((svg: SVGSVGElement) => {
        const { zoomScale: scale, svgGroupId: groupId, isKK50m: kkMode } = renderValuesRef.current;

        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.setAttribute('shape-rendering', 'geometricPrecision');
        svg.style.display = 'block';

        // Apply initial zoom immediately at injection time (correct even when shots already exist,
        // e.g. live reload, lane switch mid-series). useEffect keeps this in sync afterwards.
        const img = svg.children.namedItem(groupId);
        if (img) {
            img.setAttribute('transform', `scale(${scale}, -${scale})`);
        }
        if (scale > 1) {
            svg.querySelectorAll('text').forEach(el => { (el as SVGElement).style.display = 'none'; });
        }
        if (kkMode) {
            const labelsGroup = svg.children.namedItem(`${groupId}_labels`);
            labelsGroup?.setAttribute('transform', `scale(${scale}, ${scale})`);
        }

        // Store ref and trigger useEffect re-run via counter increment.
        // Note: circles are NOT added here — useEffect manages them exclusively.
        svgRef.current = svg;
        setSvgInjectionCount(c => c + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally empty — current values are read from renderValuesRef

    // ── Stable afterInjection callback — sizes the wrapper divs.
    // Empty deps: only uses the argument passed in, no component state.
    const handleAfterInjection = useCallback((svgOrError: SVGSVGElement | Error) => {
        if (svgOrError instanceof Error) return;
        const target = svgOrError.parentElement;
        if (target) {
            target.style.width = '100%';
            target.style.height = '100%';
            target.style.display = 'block';
            const wrapper = target.parentElement;
            if (wrapper) {
                wrapper.style.width = '100%';
                wrapper.style.height = '100%';
                wrapper.style.display = 'block';
            }
        }
    }, []);

    // Latest competition shot number (for cell highlight)
    const latestCompetitionShotNr = competitionShots.length > 0
        ? competitionShots[competitionShots.length - 1]?.shotNr
        : undefined;

    // Tile is "active" when it has a registered participant (not an empty placeholder tile).
    const isActive = laneData != null;

    // Dim empty tiles (no laneData) during Competition/Elimination phase so the focus
    // stays on lanes that are actively shooting.
    // Eliminated tiles keep full opacity — they have their own eliminated icon overlay.
    const isEmptyTile = !laneData;
    const isDimPhase =
        currentFinalPhase === FinalCompetitionPhase.Value2 ||
        currentFinalPhase === FinalCompetitionPhase.Value3;
    const opacity = isEmptyTile && isDimPhase ? 0.3 : 1;

    return (
        <div
            className={`fc-tile${isActive ? ' fc-tile--active' : ''}`}
            style={{ opacity }}
        >
            {/* SVG section: flex:1 takes remaining height after info rows */}
            <div className="fc-svg-area">
                {/* Lane number overlay */}
                {laneData?.laneNumber !== undefined && (
                    <span className="fc-lane-number">
                        {laneData.laneNumber}
                    </span>
                )}

                {/* Eliminated icon overlay (top-right) */}
                {laneData?.isEliminated && (
                    <i className="bi bi-person-x-fill fc-eliminated-icon" aria-label="Eliminated" />
                )}

                <ReactSVG
                    key={svgKey}
                    src={targetSvg}
                    wrapper="div"
                    style={REACT_SVG_WRAPPER_STYLE}
                    afterInjection={handleAfterInjection}
                    beforeInjection={handleBeforeInjection}
                />
            </div>

            {/* ── Row 1: Last shot (big, red) | spacer | Total ── */}
            <div className="fc-data-row">
                <span className="fc-last-shot">
                    {lastShot ? formatValue(lastShot.value) : '\u00A0'}
                </span>
                <span style={{ flex: 1 }} />
                {laneData?.currentTotal != null && (
                    <span className="fc-total">
                        {formatTotal(laneData.currentTotal)}
                    </span>
                )}
            </div>

            {/* ── Row 2: Rank badge | Participant name ── */}
            <div className="fc-data-row">
                {rank > 0 && (
                    <span className="fc-rank-badge">{rank}</span>
                )}
                <span className="fc-athlete-name">
                    {laneData?.participantName || '\u00A0'}
                </span>
            </div>

            {/* Competition shot grid */}
            {!isElimination && (
                <table className="fc-shot-table">
                    <tbody>
                        {Array.from({ length: gridRows }, (_, rowIdx) => (
                            <tr key={rowIdx}>
                                {Array.from({ length: 5 }, (_, colIdx) => {
                                    const shotNr = rowIdx * 5 + colIdx + 1;
                                    const shot = competitionShots.find(s => s.shotNr === shotNr);
                                    const isLatest = shot != null && shotNr === latestCompetitionShotNr;
                                    return (
                                        <td
                                            key={`${colIdx}-${shot?.value ?? ''}`}
                                            className={`fc-shot-cell${isLatest ? ' fc-shot-cell--latest' : ''}`}
                                        >
                                            <span className="fc-shot-value">
                                                {formatValue(shot?.value)}
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Elimination row */}
            {isElimination && (
                <table className="fc-elim-table">
                    <tbody>
                        <tr>
                            {(() => {
                                return Array.from({ length: 2 }, (_, i) => {
                                    const shot = currentElimPairShots[i];
                                    return (
                                        <td key={shot?.shotNr ?? i} className="fc-elim-cell">
                                            <span className="fc-elim-value">
                                                {shot ? formatValue(shot.value) : '\u00A0'}
                                            </span>
                                        </td>
                                    );
                                });
                            })()}
                        </tr>
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default FinalCompetitionTarget;

